import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { API } from "@/contexts/AuthContext";
import { EMOTION_MAP } from "@/constants/emotions";
import { Flame, Sparkles } from "lucide-react";

function useInsights() {
  return useQuery({
    queryKey: ["insights"],
    queryFn: async () => (await axios.get(`${API}/insights`)).data,
  });
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[var(--border)] p-6 text-center">
      <div className="text-3xl font-serif-display text-[var(--primary)]">{value}</div>
      <div className="text-sm text-[var(--text-muted)] mt-1">{label}</div>
    </div>
  );
}

export default function Insights() {
  const { data, isLoading } = useInsights();

  return (
    <main className="max-w-5xl mx-auto px-6 sm:px-12 py-12 sm:py-16">
      <h1 className="font-serif-display text-4xl text-[var(--primary)] mb-2">Feedback</h1>
      <p className="text-[var(--text-muted)] mb-10">A gentle look at the patterns in your Moodscape.</p>

      {isLoading && <p className="text-sm text-[var(--text-muted)]">Loading…</p>}

      {!isLoading && data && (
        <>
          <div className="bg-[var(--surface)] rounded-[1.5rem] p-8 mb-10 flex items-start gap-4">
            <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0">
              <Sparkles size={20} className="text-[var(--primary)]" />
            </div>
            <p className="text-lg font-serif-display text-[var(--text-main)] leading-snug pt-1">
              {data.reflection}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            <Stat label="Entries logged" value={data.total_entries} />
            <Stat
              label="Top emotion"
              value={data.top_emotion ? `${EMOTION_MAP[data.top_emotion]?.emoji} ${EMOTION_MAP[data.top_emotion]?.label}` : "—"}
            />
            <Stat
              label="Avg. intensity (7d)"
              value={data.avg_intensity_7d != null ? data.avg_intensity_7d.toFixed(1) : "—"}
            />
            <Stat
              label="Positive ratio (30d)"
              value={data.positive_ratio_30d != null ? `${Math.round(data.positive_ratio_30d * 100)}%` : "—"}
            />
          </div>

          <div className="bg-white rounded-[1.5rem] border border-[var(--border)] p-6 sm:p-8 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[var(--surface)] flex items-center justify-center shrink-0">
              <Flame size={20} className="text-[var(--accent)]" />
            </div>
            <div>
              <div className="text-lg font-medium">{data.current_streak_days}-day streak</div>
              <div className="text-sm text-[var(--text-muted)]">Consecutive days you've logged a mood.</div>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
